import { User } from "./model/user";
import { Item } from "./model/item";
import { DaysJson } from "./services/convert";

type Id = number;

export const API_USER_GET = `API_USER_GET`;
export const API_USER_GET_DONE = `API_USER_GET_DONE`;
export const API_USER_GET_ERROR = `API_USER_GET_ERROR`;
export const USER_LOG_OUT = `USER_LOG_OUT`;
export const API_ITEMS_GET = `API_ITEMS_GET`;
export const API_ITEMS_GET_DONE = `API_ITEMS_GET_DONE`;
export const API_ITEMS_GET_FAIL = `API_ITEMS_GET_FAIL`;
export const API_ITEMS_PATCH = `API_ITEMS_PATCH`;
export const API_ITEMS_PATCH_DONE = `API_ITEMS_PATCH_DONE`;
export const API_ITEMS_PATCH_FAIL = `API_ITEMS_PATCH_FAIL`;
export const SYNC_MARK_DIRTY = `SYNC_MARK_DIRTY`;
export const ITEM_CREATE_AFTER = `ITEM_CREATE_AFTER`;
export const ITEM_CREATE_BEFORE = `ITEM_CREATE_BEFORE`;
export const ITEM_REMOVE = `ITEM_REMOVE`;
export const ITEM_RENAME = `ITEM_RENAME`;
export const ITEM_DURATION_CHANGE = `ITEM_DURATION_CHANGE`;
export const ITEM_LOCK_CHANGE = `ITEM_LOCK_CHANGE`;
export const ITEM_ORDER_CHANGE = `ITEM_ORDER_CHANGE`;
export const UI_CALENDAR_TOGGLE = `UI_CALENDAR_TOGGLE`;
export const UI_WEEK_CHANGE = `UI_WEEK_CHANGE`;
export const UI_DAY_CHANGE = `UI_DAY_CHANGE`;
export const UI_LOG_IN_TOGGLE = `UI_LOG_IN_TOGGLE`;
export const UI_FEEDBACK_OPEN = `UI_FEEDBACK_OPEN`;
export const LOG_IN_EMAIL_CHANGE = `LOG_IN_EMAIL_CHANGE`;
export const LOG_IN_EMAIL_SEND_LINK = `LOG_IN_EMAIL_SEND_LINK`;
export const LOG_IN_GOOGLE = `LOG_IN_GOOGLE`;
export const LOG_IN_FACEBOOK = `LOG_IN_FACEBOOK`;
export const LOG_IN_TWITTER = `LOG_IN_TWITTER`;
export const LOG_IN_GITHUB = `LOG_IN_GITHUB`;
export const UNDO = `UNDO`;
export const REDO = `REDO`;

export type ApiUserGetAction = {
    type: typeof API_USER_GET;
};

export type ApiUserGetDoneAction = {
    type: typeof API_USER_GET_DONE;
    payload: User;
};

export type ApiUserGetErrorAction = {
    type: typeof API_USER_GET_ERROR;
    payload: string;
};

export type UserLogOutAction = {
    type: typeof USER_LOG_OUT;
};

export type SyncMeta = {
    // ISO datetime taken when the request started; becomes lastSyncTime
    newSyncTime: string;
    // dirty-counter snapshot of the pushed dates; {} for a pull-only GET
    pushedDates: { [date: string]: number };
};

export type ApiItemsGetAction = {
    type: typeof API_ITEMS_GET;
};

export type ApiItemsGetDoneAction = {
    type: typeof API_ITEMS_GET_DONE;
    payload: DaysJson;
    meta: SyncMeta;
};

export type ApiItemsGetFailAction = {
    type: typeof API_ITEMS_GET_FAIL;
    payload: string;
};

export type ApiItemsPatchAction = {
    type: typeof API_ITEMS_PATCH;
    payload: { dates: Array<string> };
};

export type ApiItemsPatchDoneAction = {
    type: typeof API_ITEMS_PATCH_DONE;
    payload: DaysJson;
    meta: SyncMeta;
};

export type ApiItemsPatchFailAction = {
    type: typeof API_ITEMS_PATCH_FAIL;
    payload: string;
};

export type SyncMarkDirtyAction = {
    type: typeof SYNC_MARK_DIRTY;
    payload: { dates: Array<string> };
};

export type ItemRenameAction = {
    type: typeof ITEM_RENAME;
    payload: {
        date: string;
        index: number;
        item: Item;
    };
};

export type ItemDurationChangeAction = {
    type: typeof ITEM_DURATION_CHANGE;
    payload: {
        date: string;
        index: number;
        item: Item;
    };
};

export type ItemLockChangeAction = {
    type: typeof ITEM_LOCK_CHANGE;
    payload: {
        date: string;
        index: number;
        item: Item;
    };
};

export type ItemOrderChangeAction = {
    type: typeof ITEM_ORDER_CHANGE;
    payload: {
        date: string;
        from: number;
        to: number;
    };
};

export type ItemCreateAfterAction = {
    type: typeof ITEM_CREATE_AFTER;
    payload: {
        date: string;
        index: number;
        item: Item;
    };
};

export type ItemCreateBeforeAction = {
    type: typeof ITEM_CREATE_BEFORE;
    payload: {
        date: string;
        index: number;
        item: Item;
    };
};

export type ItemRemoveAction = {
    type: typeof ITEM_REMOVE;
    payload: {
        date: string;
        index: number;
    };
};

export type UiDayChangeAction = {
    type: typeof UI_DAY_CHANGE;
    payload: {
        date: string;
    };
};

export type UiCalendarToggleAction = {
    type: typeof UI_CALENDAR_TOGGLE;
};

export type UiWeekChangeAction = {
    type: typeof UI_WEEK_CHANGE;
    payload: string;
};

export type UiLogInToggleAction = {
    type: typeof UI_LOG_IN_TOGGLE;
};

export type UndoAction = {
    type: typeof UNDO;
    payload: {
        date: string;
    };
};

export type RedoAction = {
    type: typeof REDO;
    payload: {
        date: string;
    };
};

export type Action =
    | ApiUserGetAction
    | ApiUserGetDoneAction
    | ApiUserGetErrorAction
    | UserLogOutAction
    | ApiItemsGetAction
    | ApiItemsGetDoneAction
    | ApiItemsGetFailAction
    | ApiItemsPatchAction
    | ApiItemsPatchDoneAction
    | ApiItemsPatchFailAction
    | SyncMarkDirtyAction
    | ItemRenameAction
    | ItemDurationChangeAction
    | ItemLockChangeAction
    | ItemOrderChangeAction
    | ItemCreateAfterAction
    | ItemCreateBeforeAction
    | ItemRemoveAction
    | UiDayChangeAction
    | UiCalendarToggleAction
    | UiWeekChangeAction
    | UiLogInToggleAction
    | UndoAction
    | RedoAction;

export const userGet = (): ApiUserGetAction => ({
    type: API_USER_GET,
});

export const userGetDone = (user: User): ApiUserGetDoneAction => ({
    type: API_USER_GET_DONE,
    payload: user,
});

export const userGetError = (errorMessage: string): ApiUserGetErrorAction => ({
    type: API_USER_GET_ERROR,
    payload: errorMessage,
});

export const userLogOut = (): UserLogOutAction => ({
    type: USER_LOG_OUT,
});

export const itemsGet = (): ApiItemsGetAction => ({
    type: API_ITEMS_GET,
});

export const itemsGetDone = (
    payload: DaysJson,
    meta: SyncMeta,
): ApiItemsGetDoneAction => ({
    type: API_ITEMS_GET_DONE,
    payload,
    meta,
});

export const itemsGetFail = (errorMessage: string): ApiItemsGetFailAction => ({
    type: API_ITEMS_GET_FAIL,
    payload: errorMessage,
});

export const itemsPatch = (dates: Array<string>): ApiItemsPatchAction => ({
    type: API_ITEMS_PATCH,
    payload: { dates },
});

export const itemsPatchDone = (
    payload: DaysJson,
    meta: SyncMeta,
): ApiItemsPatchDoneAction => ({
    type: API_ITEMS_PATCH_DONE,
    payload,
    meta,
});

export const itemsPatchFail = (
    errorMessage: string,
): ApiItemsPatchFailAction => ({
    type: API_ITEMS_PATCH_FAIL,
    payload: errorMessage,
});

export const syncMarkDirty = (dates: Array<string>): SyncMarkDirtyAction => ({
    type: SYNC_MARK_DIRTY,
    payload: { dates },
});

export const dayChange = (date: string): UiDayChangeAction => ({
    type: UI_DAY_CHANGE,
    payload: { date },
});

export const calendarToggle = (): UiCalendarToggleAction => ({
    type: UI_CALENDAR_TOGGLE,
});

export const logInToggle = (): UiLogInToggleAction => ({
    type: UI_LOG_IN_TOGGLE,
});

export const weekChange = (date: string): UiWeekChangeAction => ({
    type: UI_WEEK_CHANGE,
    payload: date,
});

export const itemRename = (
    date: string,
    index: number,
    item: Item,
): ItemRenameAction => ({
    type: ITEM_RENAME,
    payload: { date, index, item },
});

export const itemCreateAfter = (
    date: string,
    index: number,
    item: Item,
): ItemCreateAfterAction => ({
    type: ITEM_CREATE_AFTER,
    payload: { date, index, item },
});

export const itemCreateBefore = (
    date: string,
    index: number,
    item: Item,
): ItemCreateBeforeAction => ({
    type: ITEM_CREATE_BEFORE,
    payload: { date, index, item },
});

export const durationChange = (
    date: string,
    index: number,
    item: Item,
): ItemDurationChangeAction => ({
    type: ITEM_DURATION_CHANGE,
    payload: { date, index, item },
});

export const lockChange = (
    date: string,
    index: number,
    item: Item,
): ItemLockChangeAction => ({
    type: ITEM_LOCK_CHANGE,
    payload: { date, index, item },
});

export const remove = (date: string, index: number): ItemRemoveAction => ({
    type: ITEM_REMOVE,
    payload: { date, index },
});

export const itemOrderChange = ({
    date,
    from,
    to,
}: {
    date: string;
    from: number;
    to: number;
}): ItemOrderChangeAction => ({
    type: ITEM_ORDER_CHANGE,
    payload: { date, from, to },
});

export const undo = ({ date }: { date: string }): UndoAction => ({
    type: UNDO,
    payload: { date },
});

export const redo = ({ date }: { date: string }): RedoAction => ({
    type: REDO,
    payload: { date },
});
